import { useCallback, useState, useEffect, useRef } from 'react'
import { useCompanionManager } from '../providers/CompanionProvider'
import { VoiceRecorder } from '../voice/VoiceRecorder'
import { TranscriptionClient } from '../voice/TranscriptionClient'
import { stripSpokenMarkdown } from '../voice/spoken-text'
import { TTSPlayer } from '../voice/TTSPlayer'
import { finishPushToTalk } from '../core/push-to-talk'
import type { CompanionState, Message, PointData } from '../types/companion'

export interface UseCompanionReturn {
  /** Current state of the companion */
  state: CompanionState
  /** Conversation messages */
  messages: readonly Message[]
  /** Last point data from Claude's response */
  activePoint: PointData | null
  /** Last screenshot capture dimensions */
  captureSize: { width: number; height: number }
  /** Mic audio level 0–1 (only while listening) */
  audioLevel: number
  /** Send a text message */
  sendMessage: (text: string) => Promise<void>
  /** Start push-to-talk recording */
  startListening: () => Promise<void>
  /** Stop recording and send the transcription */
  stopListening: () => Promise<void>
  /** Dismiss the pointing cursor without stopping speech */
  dismissPoint: () => void
  /** Cancel any in-flight request and stop speech */
  cancel: () => void
  /** Clear conversation history */
  clearHistory: () => void
}

/**
 * Main hook for interacting with the companion.
 * Manages voice recording, streaming transcription, chat, and TTS playback.
 */
export function useCompanion(): UseCompanionReturn {
  const manager = useCompanionManager()

  const [state, setState] = useState<CompanionState>(manager.state)
  const [messages, setMessages] = useState<readonly Message[]>(manager.messages)
  const [activePoint, setActivePoint] = useState<PointData | null>(null)
  const audioLevelRef = useRef(0)
  const [audioLevel, setAudioLevel] = useState(0)

  const recorderRef = useRef<VoiceRecorder | null>(null)
  const transcriptionRef = useRef<TranscriptionClient | null>(null)
  const ttsRef = useRef<TTSPlayer | null>(null)
  // True only while a push-to-talk started by THIS hook is recording. The
  // manager's 'listening' state is shared with the chat voice session, so it
  // can't tell a real hold release from a stray one.
  const pttListeningRef = useRef(false)

  // Initialize voice services
  useEffect(() => {
    const config = manager.config
    recorderRef.current = new VoiceRecorder()
    transcriptionRef.current = new TranscriptionClient(config.apiBase, config.getAuthToken, {
      getKeyterms: () => manager.config.getSttKeyterms?.() ?? [],
    })
    ttsRef.current = new TTSPlayer(config.apiBase, config.getAuthToken)

    return () => {
      recorderRef.current?.cancel()
      transcriptionRef.current?.disconnect()
      ttsRef.current?.stop()
    }
  }, [manager.config.apiBase, manager.config.getAuthToken])

  const handleAssistantMessage = (message: Message, pendingPoint: PointData | null) => {
    // Chat messages don't trigger voice — text only
    if (message.role !== 'assistant' || message.source === 'chat') return

    const showPoint = () => {
      // Don't show buddy cursor when tools were used — the panel activity
      // shimmer provides the visual indicator. The cursor coordinates might
      // point at the wrong panel when multiple panels of the same type exist.
      if (message.toolCalls && message.toolCalls.length > 0) return
      if (pendingPoint) setActivePoint(pendingPoint)
    }

    // Skip messages whose audio already played via live sentence
    // streaming (direct voice dispatch) — speaking twice is worse than
    // any latency win.
    const useTts =
      message.text.trim() && manager.config.tts !== false && ttsRef.current && !message.spoken
    if (useTts) {
      ttsRef
        .current!.speak(
          stripSpokenMarkdown(message.text),
          undefined,
          () => {
            manager.startResponding()
            showPoint()
          },
          manager.detectedLanguage,
        )
        .then(
          () => manager.finishResponding(),
          () => manager.finishResponding(),
        )
    } else {
      manager.startResponding()
      showPoint()
      manager.finishResponding()
    }
  }

  // Subscribe to manager events
  useEffect(() => {
    let pendingPoint: PointData | null = null

    const unsub = manager.events.on((event) => {
      switch (event.type) {
        case 'state-change':
          setState(event.state)
          break
        case 'message':
          setMessages([...manager.messages])
          // Use point from message directly, or fall back to pendingPoint from earlier event
          handleAssistantMessage(event.message, event.message.point ?? pendingPoint)
          pendingPoint = null
          break
        case 'point':
          // Hold the point — don't show cursor yet, wait for TTS playback
          pendingPoint = event.data
          break
        case 'error':
          console.error('[companion]', event.error)
          break
      }
    })
    return unsub
  }, [manager])

  const sendMessage = useCallback(
    async (text: string) => {
      setActivePoint(null)
      await manager.sendMessage(text)
      if (manager.config.tts === false) {
        manager.finishResponding()
      }
    },
    [manager],
  )

  const startListening = useCallback(async () => {
    if (!recorderRef.current || !transcriptionRef.current) return

    // Cancel any in-flight request or playing audio from previous interaction
    ttsRef.current?.stop()
    manager.cancel()
    setActivePoint(null)
    manager.startListening()
    pttListeningRef.current = true

    try {
      // Open streaming transcription WebSocket
      await transcriptionRef.current.connect((partialText) => {
        manager.events.emit({
          type: 'transcript',
          text: partialText,
          isFinal: false,
        })
      })

      // Start recording — stream audio chunks to transcription WebSocket
      await recorderRef.current.start(
        (chunk) => {
          transcriptionRef.current?.sendAudio(chunk)
        },
        (level) => {
          audioLevelRef.current = level
          setAudioLevel(level)
        },
      )
    } catch (err) {
      // Mic denied / no device / transcription connect failed. Reset to idle
      // (manager.cancel stops the recorder + TTS) and surface a friendly error
      // instead of leaving the companion stuck in the 'listening' state.
      pttListeningRef.current = false
      manager.cancel()
      const raw = err instanceof Error ? err : new Error(String(err))
      const message =
        raw.name === 'NotAllowedError' || raw.name === 'NotFoundError'
          ? 'Microphone access is blocked. Enable it in your browser settings and try again.'
          : `Could not start voice input: ${raw.message}`
      manager.events.emit({ type: 'error', error: new Error(message) })
    }
  }, [manager])

  const stopListening = useCallback(async () => {
    const recorder = recorderRef.current
    const transcription = transcriptionRef.current
    const wasListening = pttListeningRef.current && !!recorder && !!transcription
    pttListeningRef.current = false

    try {
      await finishPushToTalk({
        wasListening,
        stopRecorder: () => {
          recorder?.stop()
          setAudioLevel(0)
        },
        // NOTE: the transcribed language is intentionally NOT used to pick the TTS
        // voice — response language is forced from the user's preference
        // (manager.detectedLanguage is set by the host app), so spoken replies match
        // the chosen language regardless of what language the user spoke in.
        stopTranscription: () => transcription!.stop(),
        // The host may switch the reply language from what was heard (a
        // Spanish question), before the gate checks the language.
        onHeard: (text) => manager.config.onHeardText?.(text, manager),
        expectedLanguage: () => manager.detectedLanguage,
        onTranscript: async (text) => {
          manager.events.emit({
            type: 'transcript',
            text,
            isFinal: true,
          })
          await sendMessage(text)
        },
        onEmpty: () => manager.cancel(),
      })
    } catch (err) {
      manager.events.emit({
        type: 'error',
        error: err instanceof Error ? err : new Error(String(err)),
      })
      manager.cancel()
    }
  }, [manager, sendMessage])

  const dismissPoint = useCallback(() => {
    setActivePoint(null)
  }, [])

  const cancel = useCallback(() => {
    pttListeningRef.current = false
    recorderRef.current?.cancel()
    transcriptionRef.current?.disconnect()
    ttsRef.current?.stop()
    manager.cancel()
    setActivePoint(null)
  }, [manager])

  const clearHistory = useCallback(() => {
    manager.clearHistory()
    setMessages([])
    setActivePoint(null)
  }, [manager])

  return {
    state,
    messages,
    activePoint,
    captureSize: manager.lastCaptureSize,
    audioLevel,
    sendMessage,
    startListening,
    stopListening,
    dismissPoint,
    cancel,
    clearHistory,
  }
}
